import { apiFetch } from '@/lib/apiFetch';
import { handleUnauthorized } from './UserManagmentServices';

// F-2 — Class Management service layer
// Wraps all /api/v1/class-management and /api/v1/student/classes endpoints.

// ---- Shared types ----

export interface ScheduleHint {
  sessionsPerWeek: number;
  nextBatchStartDate: string;
}

// ---- Hub ----

export interface HubOffering {
  offeringId: string;
  title: string;
  offeringType: string;
  confirmedCount: number;
  pendingCount: number;
  nextSessionAt: string | null;
  scheduleHint: ScheduleHint | null;
}

export async function fetchClassManagementHub(accessToken: string): Promise<HubOffering[]> {
  const response = await apiFetch('/api/v1/class-management/hub', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to fetch class management hub: ${response.status}`);
  return response.json();
}

// ---- Enrollments ----

export interface EnrollmentItem {
  id: string;
  studentUserId: string | null;
  studentName: string;
  studentEmail: string;
  status: string;
  addedAt: string;
  expiresAt: string;
  confirmedAt: string | null;
}

export async function fetchOfferingEnrollments(
  accessToken: string,
  offeringId: string,
  status?: string,
): Promise<EnrollmentItem[]> {
  const url =
    status != null
      ? `/api/v1/class-management/offerings/${offeringId}/enrollments?status=${encodeURIComponent(status)}`
      : `/api/v1/class-management/offerings/${offeringId}/enrollments`;
  const response = await apiFetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to fetch enrollments: ${response.status}`);
  return response.json();
}

export async function addEnrollment(
  accessToken: string,
  body: { threadId: string; offeringId: string },
): Promise<EnrollmentItem> {
  const response = await apiFetch('/api/v1/class-management/enrollments', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to add enrollment: ${response.status}`);
  return response.json();
}

/** Add a student directly by email — used from the Roster tab where there is no threadId. */
export async function addEnrollmentByEmail(
  accessToken: string,
  body: { studentEmail: string; offeringId: string },
): Promise<EnrollmentItem> {
  const response = await apiFetch('/api/v1/class-management/enrollments/by-email', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (response.status === 401) handleUnauthorized();
  if (response.status === 404) throw Object.assign(new Error('Student not found'), { status: 404 });
  if (response.status === 409) throw Object.assign(new Error('Already enrolled'), { status: 409 });
  if (!response.ok) throw new Error(`Failed to add enrollment: ${response.status}`);
  return response.json();
}

export async function confirmEnrollment(
  accessToken: string,
  enrollmentId: string,
): Promise<EnrollmentItem> {
  const response = await apiFetch(
    `/api/v1/class-management/enrollments/${enrollmentId}/confirm`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to confirm enrollment: ${response.status}`);
  return response.json();
}

export async function removeEnrollment(
  accessToken: string,
  enrollmentId: string,
): Promise<void> {
  const response = await apiFetch(
    `/api/v1/class-management/enrollments/${enrollmentId}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
  if (response.status === 401) handleUnauthorized();
  if (response.status === 204) return;
  if (!response.ok) throw new Error(`Failed to remove enrollment: ${response.status}`);
}

// ---- Sessions ----

export interface SessionItem {
  id: string;
  scheduledStart: string;
  durationMinutes: number;
  origin: string;
  status: string;
  summarySentAt: string | null;
}

export interface SessionListResponse {
  scheduleDefined: boolean;
  scheduleHint: ScheduleHint | null;
  sessions: SessionItem[];
}

export interface ScheduleWarning {
  message: string;
  hintedTime: string;
}

export interface SessionCreateResponse {
  session: SessionItem;
  scheduleWarning: ScheduleWarning | null;
}

export interface AttendanceEntry {
  enrollmentId: string;
  studentName: string;
  status: 'PRESENT' | 'ABSENT' | 'LATE' | null;
}

export interface SessionDetailResponse {
  session: SessionItem;
  notes: string | null;
  summary: string | null;
  summarySentAt: string | null;
  summarySentCount: number | null;
  summarySkippedCount: number | null;
  attendance: AttendanceEntry[];
  scheduleWarning: null;
}

export interface AttendanceResult {
  enrollmentId: string;
  status: string;
  markedAt: string;
}

export interface NotesSummaryResult {
  body: string;
  updatedAt: string;
}

export interface SummarySendResult {
  sentCount: number;
  skippedCount: number;
  skipped: string[];
  sentAt: string;
}

export async function fetchOfferingSessions(
  accessToken: string,
  offeringId: string,
): Promise<SessionListResponse> {
  const response = await apiFetch(
    `/api/v1/class-management/offerings/${offeringId}/sessions`,
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to fetch sessions: ${response.status}`);
  const raw = await response.json();
  // Backend may return a plain array (old) or the wrapped object (new).
  if (Array.isArray(raw)) {
    return { scheduleDefined: false, scheduleHint: null, sessions: raw as SessionItem[] };
  }
  return raw as SessionListResponse;
}

export async function createSession(
  accessToken: string,
  offeringId: string,
  body: { scheduledStart: string; durationMinutes: number },
): Promise<SessionCreateResponse> {
  const response = await apiFetch(
    `/api/v1/class-management/offerings/${offeringId}/sessions`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
  );
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to create session: ${response.status}`);
  return response.json();
}

export async function fetchSessionDetail(
  accessToken: string,
  sessionId: string,
): Promise<SessionDetailResponse> {
  const response = await apiFetch(`/api/v1/class-management/sessions/${sessionId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to fetch session detail: ${response.status}`);
  return response.json();
}

export async function markAttendance(
  accessToken: string,
  sessionId: string,
  enrollmentId: string,
  body: { status: 'PRESENT' | 'ABSENT' | 'LATE' },
): Promise<AttendanceResult> {
  const response = await apiFetch(
    `/api/v1/class-management/sessions/${sessionId}/attendance/${enrollmentId}`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
  );
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to mark attendance: ${response.status}`);
  return response.json();
}

export async function upsertSessionNotes(
  accessToken: string,
  sessionId: string,
  body: { body: string },
): Promise<NotesSummaryResult> {
  const response = await apiFetch(
    `/api/v1/class-management/sessions/${sessionId}/notes`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      // Backend expects { content } — map from the frontend's { body }
      body: JSON.stringify({ content: body.body }),
    },
  );
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to upsert notes: ${response.status}`);
  return response.json();
}

export async function upsertSessionSummary(
  accessToken: string,
  sessionId: string,
  body: { body: string },
): Promise<NotesSummaryResult> {
  const response = await apiFetch(
    `/api/v1/class-management/sessions/${sessionId}/summary`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
  );
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to upsert summary: ${response.status}`);
  return response.json();
}

export async function sendSessionSummary(
  accessToken: string,
  sessionId: string,
): Promise<SummarySendResult> {
  const response = await apiFetch(
    `/api/v1/class-management/sessions/${sessionId}/summary/send`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to send summary: ${response.status}`);
  return response.json();
}

export async function cancelSession(
  accessToken: string,
  sessionId: string,
  notifyStudents: boolean,
): Promise<void> {
  const response = await apiFetch(
    `/api/v1/class-management/sessions/${sessionId}/cancel`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ notifyStudents }),
    },
  );
  if (response.status === 401) handleUnauthorized();
  if (response.status === 204) return;
  if (!response.ok) throw new Error(`Failed to cancel session: ${response.status}`);
}

export async function rescheduleSession(
  accessToken: string,
  sessionId: string,
  body: { scheduledStart: string; durationMinutes: number; notifyStudents: boolean },
): Promise<SessionItem> {
  const response = await apiFetch(
    `/api/v1/class-management/sessions/${sessionId}/reschedule`,
    {
      method: 'PUT',
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to reschedule session: ${response.status}`);
  return response.json();
}

// ---- Broadcasts ----

export interface BroadcastResult {
  id: string;
  offeringId: string;
  subject: string;
  body: string;
  sentCount: number;
  skippedCount: number;
  createdAt: string;
  expiresAt: string | null;
  capExceeded: boolean;
}

export async function sendBroadcast(
  accessToken: string,
  offeringId: string,
  body: { subject: string; body: string; expiresAt?: string },
): Promise<BroadcastResult> {
  const response = await apiFetch(
    `/api/v1/class-management/offerings/${offeringId}/broadcasts`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
  );
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to send broadcast: ${response.status}`);
  return response.json();
}

export async function fetchTeacherBroadcasts(
  accessToken: string,
  offeringId: string,
): Promise<BroadcastResult[]> {
  const response = await apiFetch(
    `/api/v1/class-management/offerings/${offeringId}/broadcasts`,
    { headers: { Authorization: `Bearer ${accessToken}` } },
  );
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to fetch broadcasts: ${response.status}`);
  return response.json();
}

export async function editBroadcast(
  accessToken: string,
  broadcastId: string,
  body: { subject: string; body: string; expiresAt?: string },
): Promise<BroadcastResult> {
  const response = await apiFetch(
    `/api/v1/class-management/broadcasts/${broadcastId}`,
    {
      method: 'PUT',
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to edit broadcast: ${response.status}`);
  return response.json();
}

export async function deleteBroadcast(
  accessToken: string,
  broadcastId: string,
): Promise<void> {
  const response = await apiFetch(
    `/api/v1/class-management/broadcasts/${broadcastId}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
  if (response.status === 401) handleUnauthorized();
  if (response.status === 204) return;
  if (!response.ok) throw new Error(`Failed to delete broadcast: ${response.status}`);
}

// ---- Student: my classes ----

export interface SummaryItem {
  sessionId: string;
  body: string;
  sentAt: string;
}

export interface StudentClassItem {
  offeringId: string;
  title: string;
  offeringType: string;
  teacherName: string | null;
  status: string;
  addedAt: string;
  totalSessions: number;
  upcomingSessions: number;
  nextSessionAt: string | null;
  summaries: SummaryItem[];
}

export async function fetchMyClasses(accessToken: string): Promise<StudentClassItem[]> {
  const response = await apiFetch('/api/v1/student/classes', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to fetch my classes: ${response.status}`);
  return response.json();
}

export interface StudentBroadcastItem {
  id: string;
  subject: string;
  body: string;
  sentAt: string;
  expiresAt: string | null;
}

export async function fetchStudentClassSessions(
  accessToken: string,
  offeringId: string,
): Promise<SessionItem[]> {
  const response = await apiFetch(`/api/v1/student/classes/${offeringId}/sessions`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to fetch sessions: ${response.status}`);
  return response.json();
}

export async function fetchStudentClassBroadcasts(
  accessToken: string,
  offeringId: string,
): Promise<StudentBroadcastItem[]> {
  const response = await apiFetch(`/api/v1/student/classes/${offeringId}/broadcasts`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to fetch broadcasts: ${response.status}`);
  return response.json();
}

// ---- Student: attendance ----

export type AttendanceStatus = 'PRESENT' | 'ABSENT' | 'LATE' | 'NOT_MARKED';

export interface SessionAttendanceItem {
  sessionId: string;
  scheduledStart: string;
  durationMinutes: number;
  status: AttendanceStatus;
}

export interface StudentAttendanceReport {
  totalSessions: number;
  attended: number;
  absent: number;
  late: number;
  notMarked: number;
  sessions: SessionAttendanceItem[];
}

export async function fetchMyClassAttendance(
  accessToken: string,
  offeringId: string,
): Promise<StudentAttendanceReport> {
  const response = await apiFetch(`/api/v1/student/classes/${offeringId}/attendance`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to fetch attendance: ${response.status}`);
  return response.json();
}
